import type { OverlayRef } from '@angular/cdk/overlay';
import { isPlatformBrowser } from '@angular/common';
import { EventEmitter, Inject, PLATFORM_ID } from '@angular/core';

import { filter, fromEvent, Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';

import type { ZardDialogComponent, ZardDialogOptions } from './dialog.component';

const enum eTriggerAction {
  CANCEL = 'cancel',
  OK = 'ok',
}

export class ZardDialogRef<T = any, R = any, U = any> {
  private destroy$ = new Subject<void>();
  private readonly afterClosed$ = new ReplaySubject<R | undefined>(1);
  private isClosing = false;
  protected result?: R;
  componentInstance: T | null = null;

  constructor(
    private readonly overlayRef: OverlayRef | undefined,
    private config: ZardDialogOptions<T, U>,
    private readonly containerInstance: ZardDialogComponent<T, U> | undefined,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    if (this.containerInstance) {
      this.containerInstance.cancelTriggered.subscribe(() => this.trigger(eTriggerAction.CANCEL));
      this.containerInstance.okTriggered.subscribe(() => this.trigger(eTriggerAction.OK));
    }

    if ((this.config.zMaskClosable ?? true) && isPlatformBrowser(this.platformId) && this.overlayRef) {
      this.overlayRef
        .outsidePointerEvents()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.close());
    }

    if (isPlatformBrowser(this.platformId)) {
      fromEvent<KeyboardEvent>(document, 'keydown')
        .pipe(
          filter(event => event.key === 'Escape'),
          takeUntil(this.destroy$),
        )
        .subscribe(() => this.close());
    }
  }

  afterClosed(): Observable<R | undefined> {
    return this.afterClosed$.asObservable();
  }

  close(result?: R) {
    if (this.isClosing) {
      return;
    }

    this.isClosing = true;
    this.result = result;

    const finalize = () => {
      if (this.overlayRef) {
        if (this.overlayRef.hasAttached()) {
          this.overlayRef.detachBackdrop();
        }
        this.overlayRef.dispose();
      }

      if (!this.afterClosed$.closed) {
        this.afterClosed$.next(result);
        this.afterClosed$.complete();
      }

      if (!this.destroy$.closed) {
        this.destroy$.next();
        this.destroy$.complete();
      }
    };

    if (!this.overlayRef || !isPlatformBrowser(this.platformId) || !this.containerInstance) {
      finalize();
      return;
    }

    const hostElement = this.containerInstance.getNativeElement();
    hostElement.classList.add('dialog-leave');

    setTimeout(finalize, 150);
  }

  private trigger(action: eTriggerAction) {
    const trigger = { ok: this.config.zOnOk, cancel: this.config.zOnCancel }[action];

    if (trigger instanceof EventEmitter) {
      trigger.emit(this.getContentComponent());
    } else if (typeof trigger === 'function') {
      const result = trigger(this.getContentComponent()) as R;
      this.closeWithResult(result);
    } else {
      this.close();
    }
  }

  private getContentComponent(): T {
    return this.componentInstance as T;
  }

  private closeWithResult(result: R): void {
    if (result !== false) {
      this.close(result);
    }
  }
}
